import { useEffect, useRef, useState, useCallback } from 'react'
import { createConsumer, type Cable } from '@rails/actioncable'
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types'

interface ElementDef {
  type: string
  x: number
  y: number
  width?: number | string
  height?: number | string
  text?: string
  label?: string
  centerInId?: string
  id?: string
  startId?: string
  endId?: string
  startPoint?: { x: number; y: number }
  endPoint?: { x: number; y: number }
  properties?: Record<string, unknown>
  // Layout container children can be nested layout containers
  layout?: 'vertical' | 'horizontal'
  align?: 'start' | 'center' | 'end'
  justify?: 'start' | 'center' | 'end'
  gap?: number
  padding?: number
  preset?: string
  children?: ElementDef[]
}

interface ExcalidrawCommand {
  action: string
  type?: string
  x?: number
  y?: number
  width?: number | string
  height?: number | string
  text?: string
  label?: string
  centerInId?: string
  id?: string
  ids?: string[]
  groupId?: string
  elements?: ElementDef[]
  properties?: Record<string, unknown>
  alignment?: string
  direction?: string
  offsetX?: number
  offsetY?: number
  textContains?: string
  bounds?: { x: number; y: number; width: number; height: number }
  zoom?: number
  scrollToContent?: boolean
  name?: string
  startId?: string
  endId?: string
  startPoint?: { x: number; y: number }
  endPoint?: { x: number; y: number }
  // Auto-layout container properties
  layout?: 'vertical' | 'horizontal'
  gap?: number
  padding?: number
  preset?: string
  children?: ElementDef[]
  // Cross-axis alignment for layout containers
  align?: 'start' | 'center' | 'end'
  // Main-axis alignment (justify)
  justify?: 'start' | 'center' | 'end'
  // Find empty space
  findDirection?: 'right' | 'below' | 'auto'
}

interface UseExcalidrawUseOptions {
  projectId: number
  excalidrawAPI: ExcalidrawImperativeAPI | null
  onCommandsExecuted?: () => void
  onSwitchToSketch?: () => Promise<boolean>
}

// Snapshot storage (in-memory for POC)
const snapshots: Record<string, { elements: readonly any[]; appState: any }> = {}

// ---------- Auto-layout registry ----------
interface LayoutMetadata {
  layout: 'vertical' | 'horizontal'
  align: 'start' | 'center' | 'end'
  justify: 'start' | 'center' | 'end'
  gap: number
  padding: number
  childIds: string[]
  fillDirectives: Map<string, { width?: boolean; height?: boolean }>
  fitContent: boolean
}
const layoutRegistry = new Map<string, LayoutMetadata>()

/**
 * Reposition children of a layout container according to stored metadata.
 * Returns the updated elements array (caller must call api.updateScene).
 */
function applyLayout(elements: any[], containerId: string): any[] {
  const meta = layoutRegistry.get(containerId)
  if (!meta) return elements

  // Build index map for O(1) lookups instead of O(n) findIndex per child
  const idxMap = new Map<string, number>()
  for (let i = 0; i < elements.length; i++) {
    if (!elements[i].isDeleted) idxMap.set(elements[i].id, i)
  }

  const cIdx = idxMap.get(containerId)
  if (cIdx == null) return elements
  const container = elements[cIdx]

  const { layout, align, justify, gap, padding, childIds, fillDirectives } = meta
  const cw = container.width || 0
  const ch = container.height || 0

  // Calculate remaining space for fill_container in the main axis direction
  // In vertical layout: fill_width = full inner width (standard), fill_height = remaining vertical space
  // In horizontal layout: fill_width = remaining horizontal space, fill_height = full inner height (standard)
  let fixedMainAxisSize = 0
  let fillMainAxisCount = 0
  for (const cid of childIds) {
    const cidx = idxMap.get(cid)
    if (cidx == null) continue
    const cfill = fillDirectives.get(cid)
    if (layout === 'horizontal') {
      if (cfill?.width) { fillMainAxisCount++ } else { fixedMainAxisSize += (elements[cidx].width || 0) }
    } else {
      if (cfill?.height) { fillMainAxisCount++ } else { fixedMainAxisSize += (elements[cidx].height || 0) }
    }
  }
  const totalGaps = Math.max(0, childIds.length - 1) * gap
  const mainAxisSpace = (layout === 'horizontal' ? cw : ch) - 2 * padding - fixedMainAxisSize - totalGaps
  const fillMainAxisEach = fillMainAxisCount > 0 ? Math.max(0, mainAxisSpace / fillMainAxisCount) : 0

  let cursor = layout === 'vertical'
    ? container.y + padding
    : container.x + padding

  for (const childId of childIds) {
    const idx = idxMap.get(childId)
    if (idx == null) continue

    const child = { ...elements[idx] }
    const fill = fillDirectives.get(childId)

    if (layout === 'vertical') {
      if (fill?.width) child.width = Math.max(0, cw - 2 * padding)
      if (fill?.height) child.height = Math.max(0, fillMainAxisEach)
    } else {
      if (fill?.width) child.width = Math.max(0, fillMainAxisEach)
      if (fill?.height) child.height = Math.max(0, ch - 2 * padding)
    }

    if (layout === 'vertical') {
      // Cross-axis alignment (horizontal position)
      if (align === 'center') {
        child.x = container.x + (cw - (child.width || 0)) / 2
      } else if (align === 'end') {
        child.x = container.x + cw - padding - (child.width || 0)
      } else {
        child.x = container.x + padding
      }
      child.y = cursor
      cursor += (child.height || 0) + gap
    } else {
      child.x = cursor
      // Cross-axis alignment (vertical position)
      if (align === 'center') {
        child.y = container.y + (ch - (child.height || 0)) / 2
      } else if (align === 'end') {
        child.y = container.y + ch - padding - (child.height || 0)
      } else {
        child.y = container.y + padding
      }
      cursor += (child.width || 0) + gap
    }

    elements[idx] = { ...child, version: (child.version || 1) + 1, updated: Date.now() }
  }

  // Reposition ALL bound text elements (text with containerId matching a moved child)
  // Uses containerId scan instead of boundElements property — more robust across nested containers
  for (const childId of childIds) {
    const childIdx = idxMap.get(childId)
    if (childIdx == null) continue
    const child = elements[childIdx]
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].containerId === childId && elements[i].type === 'text' && !elements[i].isDeleted) {
        const textEl = elements[i]
        const textAlign = textEl.textAlign || 'center'
        let textX: number
        if (textAlign === 'left') {
          textX = child.x + 10
        } else if (textAlign === 'right') {
          textX = child.x + (child.width || 0) - (textEl.width || 0) - 10
        } else {
          textX = child.x + ((child.width || 0) - (textEl.width || 0)) / 2
        }
        elements[i] = {
          ...textEl,
          x: textX,
          y: child.y + ((child.height || 0) - (textEl.height || 0)) / 2,
          version: (textEl.version || 1) + 1,
          updated: Date.now(),
        }
      }
    }
  }

  // Auto-resize: grow if overflow, shrink only if fitContent is set
  const totalContent = cursor - gap
  const fitContent = meta.fitContent
  if (layout === 'vertical') {
    const fittedH = totalContent - container.y + padding
    if (fittedH > ch || fitContent) {
      elements[cIdx] = {
        ...elements[cIdx],
        height: Math.max(padding * 2, fittedH),
        version: (elements[cIdx].version || 1) + 1,
        updated: Date.now(),
      }
    }
  } else {
    const fittedW = totalContent - container.x + padding
    if (fittedW > cw || fitContent) {
      elements[cIdx] = {
        ...elements[cIdx],
        width: Math.max(padding * 2, fittedW),
        version: (elements[cIdx].version || 1) + 1,
        updated: Date.now(),
      }
    }
  }

  // Justify: shift all children along the main axis (vertical: y-shift, horizontal: x-shift)
  if (justify === 'center' || justify === 'end') {
    const updatedContainer = elements[cIdx]
    const containerSize = layout === 'vertical' ? (updatedContainer.height || 0) : (updatedContainer.width || 0)
    const contentEnd = totalContent - (layout === 'vertical' ? container.y : container.x)
    const remainingSpace = containerSize - contentEnd - padding
    if (remainingSpace > 0) {
      const offset = justify === 'center' ? remainingSpace / 2 : remainingSpace
      for (const childId of childIds) {
        const idx = idxMap.get(childId)
        if (idx == null) continue
        if (layout === 'vertical') {
          elements[idx] = { ...elements[idx], y: elements[idx].y + offset }
        } else {
          elements[idx] = { ...elements[idx], x: elements[idx].x + offset }
        }
        // Also shift bound text
        for (let i = 0; i < elements.length; i++) {
          if (elements[i].containerId === childId && elements[i].type === 'text' && !elements[i].isDeleted) {
            if (layout === 'vertical') {
              elements[i] = { ...elements[i], y: elements[i].y + offset }
            } else {
              elements[i] = { ...elements[i], x: elements[i].x + offset }
            }
          }
        }
      }
    }
  }

  return elements
}

/**
 * Track fill_container directives for a child element.
 */
function trackFillDirective(
  fillDirectives: Map<string, { width?: boolean; height?: boolean }>,
  childId: string,
  widthValue: unknown,
  heightValue: unknown,
): { hasFillW: boolean; hasFillH: boolean } {
  const hasFillW = widthValue === 'fill_container'
  const hasFillH = heightValue === 'fill_container'
  if (hasFillW || hasFillH) {
    fillDirectives.set(childId, { width: hasFillW, height: hasFillH })
  }
  return { hasFillW, hasFillH }
}

/**
 * Calculate bounding box of a set of elements.
 */
function getColorLuminance(hex: string): number | null {
  if (!hex || hex === 'transparent') return null
  const clean = hex.replace('#', '')
  if (clean.length < 6) return null
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return 0.299 * r + 0.587 * g + 0.114 * b
}

function isDarkColor(hex: string): boolean {
  const lum = getColorLuminance(hex)
  return lum !== null && lum < 80
}

function isLightColor(hex: string): boolean {
  const lum = getColorLuminance(hex)
  return lum !== null && lum > 180
}

function getContrastFixedColor(bgColor: string, textColor: string | undefined): string | null {
  if (!textColor) return isDarkColor(bgColor) ? '#e4e4e7' : '#1e1e1e'
  if (isDarkColor(bgColor) && isDarkColor(textColor)) return '#fafafa'
  if (isLightColor(bgColor) && isLightColor(textColor)) return '#09090b'
  return null
}

function getOverlapArea(a: any, b: any): number {
  const overlapX = Math.max(0, Math.min(a.x + (a.width || 0), b.x + (b.width || 0)) - Math.max(a.x, b.x))
  const overlapY = Math.max(0, Math.min(a.y + (a.height || 0), b.y + (b.height || 0)) - Math.max(a.y, b.y))
  return overlapX * overlapY
}

function getElementsBounds(elements: any[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const el of elements) {
    minX = Math.min(minX, el.x)
    minY = Math.min(minY, el.y)
    maxX = Math.max(maxX, el.x + (el.width || 0))
    maxY = Math.max(maxY, el.y + (el.height || 0))
  }
  return { minX, minY, maxX, maxY }
}

// Preset defaults for common container types
const PRESETS: Record<string, Record<string, any>> = {
  page: { align: 'center', justify: 'center', padding: 80, containerProperties: { roundness: null } },
  card: { align: 'start', padding: 24, fitContent: true, containerProperties: { backgroundColor: '#ffffff', strokeColor: '#e4e4e7', roundness: { type: 3 } } },
  sidebar: { align: 'start', padding: 16, labelTextAlign: 'left', containerProperties: { backgroundColor: '#f4f4f5', strokeColor: '#e4e4e7', roundness: null } },
}

/**
 * Recursively build layout container skeletons, handling nested containers.
 * Returns all skeletons (container + children + nested containers' elements)
 * and a list of container IDs in top-down order for applying layout.
 */
function buildLayoutTree(
  def: {
    x: number; y: number; width: number; height: number;
    layout: string; align?: string; justify?: string; gap?: number; padding?: number; preset?: string;
    properties?: Record<string, unknown>; children?: ElementDef[]; id?: string;
    fitContent?: boolean; isNested?: boolean;
  },
): { skeletons: any[]; containerIds: string[] } {
  // Apply preset defaults (explicit values override preset)
  const preset = def.preset ? PRESETS[def.preset] : null
  const presetProps = preset?.containerProperties || {}

  const containerId = def.id || generateId()
  const containerW = def.width
  const containerH = def.height
  const gap = def.gap ?? 16
  const padding = def.padding ?? preset?.padding ?? (def.isNested ? 0 : 24)
  const layout = def.layout as 'vertical' | 'horizontal'
  const align = (def.align || preset?.align || 'start') as 'start' | 'center' | 'end'
  const justify = (def.justify || preset?.justify || 'start') as 'start' | 'center' | 'end'
  const childDefs = def.children || []

  const fillDirectives = new Map<string, { width?: boolean; height?: boolean }>()
  const childIds: string[] = []
  const allSkeletons: any[] = []
  const nestedContainerIds: string[] = []

  // Detect background for auto-contrast text color
  const parentBg = (def.properties?.backgroundColor as string) || '#ffffff'

  for (const childDef of childDefs) {
    const childId = childDef.id || generateId()
    childIds.push(childId)

    // Auto-fill: in horizontal rows with gap=0, children without explicit width
    // get promoted to fill_container so they distribute equally (table row behavior)
    const autoFillW = layout === 'horizontal' && gap === 0 && childDef.width === undefined
    const effectiveWidth = autoFillW ? 'fill_container' as string | number | undefined : childDef.width

    // Check if this child is a nested layout container
    if (childDef.layout && childDef.children) {
      const nestedW = typeof effectiveWidth === 'number' ? effectiveWidth : containerW - 2 * padding
      const nestedH = typeof childDef.height === 'number' ? childDef.height : 40
      const hasFillW = effectiveWidth === 'fill_container'
      const hasFillH = childDef.height === 'fill_container'
      if (hasFillW || hasFillH) {
        fillDirectives.set(childId, { width: hasFillW, height: hasFillH })
      }

      const nestedFitContent = childDef.height === 'fit_content'
      const nested = buildLayoutTree({
        x: def.x + padding,
        y: def.y + padding,
        width: hasFillW ? containerW - 2 * padding : nestedW,
        height: hasFillH ? containerH - 2 * padding : (nestedFitContent ? 600 : nestedH),
        layout: childDef.layout,
        align: childDef.align as string | undefined,
        gap: childDef.gap,
        padding: childDef.padding,
        properties: childDef.properties,
        children: childDef.children,
        id: childId,
        fitContent: nestedFitContent,
        preset: childDef.preset,
        isNested: true,
      })
      allSkeletons.push(...nested.skeletons)
      nestedContainerIds.push(...nested.containerIds)
    } else {
      // Flat child element
      const { hasFillW, hasFillH } = trackFillDirective(fillDirectives, childId, effectiveWidth, childDef.height)
      const resolvedW = hasFillW ? containerW - 2 * padding : (typeof effectiveWidth === 'number' ? effectiveWidth : undefined)
      const resolvedH = hasFillH ? containerH - 2 * padding : (typeof childDef.height === 'number' ? childDef.height : undefined)

      // Auto-fix text color for contrast
      const childProps = childDef.properties || {}
      // Inherit labelTextAlign from parent properties or preset
      const inheritedLabelAlign = (def.properties?.labelTextAlign as string) || preset?.labelTextAlign
      let autoProps = { ...(inheritedLabelAlign && !childProps.labelTextAlign ? { labelTextAlign: inheritedLabelAlign } : {}), ...childProps }
      if (childDef.type === 'text' || childDef.label) {
        const textColor = (childProps.strokeColor as string) || (childProps.labelColor as string)
        const fixedColor = getContrastFixedColor(parentBg, textColor)
        if (fixedColor && fixedColor !== textColor) {
          autoProps = { ...childProps, strokeColor: fixedColor }
        }
      }

      const skeleton = buildSkeleton({
        ...childDef,
        action: 'create_element',
        id: childId,
        x: def.x + padding,
        y: def.y + padding,
        width: resolvedW,
        height: resolvedH,
        properties: autoProps,
      } as ExcalidrawCommand)
      if (!skeleton.id) skeleton.id = childId
      allSkeletons.push(skeleton)
    }
  }

  // Build this container's skeleton
  const containerSkeleton = buildSkeleton({
    action: 'create_element',
    type: 'rectangle',
    id: containerId,
    x: def.x,
    y: def.y,
    width: containerW,
    height: containerH,
    properties: {
      ...(def.isNested
        ? { backgroundColor: 'transparent', strokeColor: 'transparent', roundness: null }
        : { backgroundColor: '#ffffff', strokeColor: '#e4e4e7', roundness: { type: 3 } }
      ),
      ...presetProps,
      ...(def.properties || {}),
    },
  } as ExcalidrawCommand)
  if (!containerSkeleton.id) containerSkeleton.id = containerId

  // Store layout metadata
  const fitContent = def.fitContent || (preset?.fitContent && def.height == null) || false
  layoutRegistry.set(containerId, { layout, align, justify, gap, padding, childIds, fillDirectives, fitContent })

  // Return: container first (z-order), then children/nested elements
  // containerIds: this container first, then nested (top-down order for applyLayout)
  return {
    skeletons: [containerSkeleton, ...allSkeletons],
    containerIds: [containerId, ...nestedContainerIds],
  }
}

// Lazy-loaded reference to convertToExcalidrawElements
let _convertToExcalidrawElements: ((skeletons: any[], opts?: { regenerateIds: boolean }) => any[]) | null = null

async function getConvertFn() {
  if (!_convertToExcalidrawElements) {
    const mod = await import('@excalidraw/excalidraw')
    _convertToExcalidrawElements = mod.convertToExcalidrawElements
  }
  return (skeletons: any[]) => _convertToExcalidrawElements!(skeletons, { regenerateIds: false })
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
}

/**
 * Build an element skeleton from a command.
 * For shapes with `label`, embeds a nested label object that
 * convertToExcalidrawElements uses to create a bound text element.
 */
function buildSkeleton(cmd: ExcalidrawCommand): any {
  const props = cmd.properties || {}

  const base: Record<string, unknown> = {
    x: cmd.x || 0,
    y: cmd.y || 0,
    strokeColor: props.strokeColor ?? '#1e1e1e',
    backgroundColor: props.backgroundColor ?? 'transparent',
    fillStyle: 'solid',
    strokeWidth: props.strokeWidth ?? 1,
    roughness: props.roughness ?? 0,
    opacity: props.opacity ?? 100,
  }

  // Propagate temp ID for cross-referencing within a batch
  if (cmd.id) base.id = cmd.id

  if (props.roundness !== undefined) base.roundness = props.roundness

  // Only set width/height if provided and numeric (let Excalidraw auto-size text)
  if (cmd.width != null && typeof cmd.width === 'number') base.width = cmd.width
  if (cmd.height != null && typeof cmd.height === 'number') base.height = cmd.height

  switch (cmd.type) {
    case 'text':
      return {
        ...base,
        type: 'text',
        text: cmd.text || '',
        fontSize: (props.fontSize as number) || 20,
        fontFamily: (props.fontFamily as number) || 2,
        textAlign: (props.textAlign as string) || 'left',
        verticalAlign: 'top',
        roundness: null,
      }

    case 'arrow':
    case 'line': {
      const w = (typeof cmd.width === 'number' ? cmd.width : 0) || 100
      const h = (typeof cmd.height === 'number' ? cmd.height : 0) || 0
      const endX = cmd.endPoint?.x ?? w
      const endY = cmd.endPoint?.y ?? h
      const startX = cmd.startPoint?.x ?? 0
      const startY = cmd.startPoint?.y ?? 0
      return {
        ...base,
        type: cmd.type,
        width: Math.abs(endX - startX) || w,
        height: Math.abs(endY - startY) || 1,
        points: [[0, 0], [endX - startX, endY - startY]],
        startArrowhead: null,
        endArrowhead: cmd.type === 'arrow' ? 'arrow' : null,
        roundness: null,
      }
    }

    case 'freedraw':
      return {
        ...base,
        type: 'freedraw',
        points: [[0, 0]],
        pressures: [],
        simulatePressure: true,
        roundness: null,
      }

    case 'rectangle':
    case 'diamond':
    case 'ellipse':
    default: {
      const shapeType = cmd.type || 'rectangle'
      const labelText = cmd.label || (props.label as string)

      const skeleton: Record<string, unknown> = {
        ...base,
        type: shapeType,
        width: (typeof cmd.width === 'number' ? cmd.width : 0) || 100,
        height: (typeof cmd.height === 'number' ? cmd.height : 0) || 100,
        roundness: props.roundness !== undefined ? props.roundness : { type: 3 },
      }

      if (labelText) {
        const minLabelWidth = Math.max(100, labelText.length * 10 + 32)
        if ((skeleton.width as number) < minLabelWidth) {
          skeleton.width = minLabelWidth
        }
        skeleton.label = {
          text: labelText,
          textAlign: (props.labelTextAlign as string) || 'center',
          verticalAlign: 'middle',
          fontSize: (props.fontSize as number) || 14,
          fontFamily: (props.fontFamily as number) || 2,
          strokeColor: (props.labelColor as string) || (props.strokeColor as string) || '#09090b',
        }
      }

      return skeleton
    }
  }
}

/**
 * Calculate the point on an element's edge where a line from its center
 * toward a target point would intersect. Used for edge-to-edge arrows.
 */
function getEdgePoint(el: any, target: { x: number; y: number }): { x: number; y: number } {
  const cx = el.x + (el.width || 0) / 2
  const cy = el.y + (el.height || 0) / 2
  const dx = target.x - cx
  const dy = target.y - cy

  if (dx === 0 && dy === 0) return { x: cx, y: cy }

  const halfW = (el.width || 0) / 2
  const halfH = (el.height || 0) / 2
  const scaleX = halfW > 0 ? halfW / Math.abs(dx) : Infinity
  const scaleY = halfH > 0 ? halfH / Math.abs(dy) : Infinity
  const scale = Math.min(scaleX, scaleY)

  return { x: cx + dx * scale, y: cy + dy * scale }
}

/**
 * Build an arrow skeleton with bindings to existing elements.
 * Calculates edge-to-edge points (nearest edges) instead of center-to-center.
 */
function buildBoundArrow(cmd: ExcalidrawCommand, existingElements: readonly any[]): any {
  const props = cmd.properties || {}
  const startEl = cmd.startId ? existingElements.find(e => e.id === cmd.startId && !e.isDeleted) : null
  const endEl = cmd.endId ? existingElements.find(e => e.id === cmd.endId && !e.isDeleted) : null

  const startCenter = startEl
    ? { x: startEl.x + (startEl.width || 0) / 2, y: startEl.y + (startEl.height || 0) / 2 }
    : { x: cmd.x || 0, y: cmd.y || 0 }

  const endCenter = endEl
    ? { x: endEl.x + (endEl.width || 0) / 2, y: endEl.y + (endEl.height || 0) / 2 }
    : cmd.endPoint
      ? { x: (cmd.x || 0) + cmd.endPoint.x, y: (cmd.y || 0) + cmd.endPoint.y }
      : { x: (cmd.x || 0) + ((typeof cmd.width === 'number' ? cmd.width : 0) || 100), y: (cmd.y || 0) + (typeof cmd.height === 'number' ? cmd.height : 0) }

  // Calculate edge intersection points for each bound element
  const startPt = startEl ? getEdgePoint(startEl, endCenter) : startCenter
  const endPt = endEl ? getEdgePoint(endEl, startCenter) : endCenter

  const dx = endPt.x - startPt.x
  const dy = endPt.y - startPt.y

  return {
    type: cmd.type || 'arrow',
    x: startPt.x,
    y: startPt.y,
    width: Math.abs(dx) || 1,
    height: Math.abs(dy) || 1,
    points: [[0, 0], [dx, dy]],
    strokeColor: props.strokeColor ?? '#1e1e1e',
    backgroundColor: props.backgroundColor ?? 'transparent',
    fillStyle: 'solid',
    strokeWidth: props.strokeWidth ?? 1,
    roughness: props.roughness ?? 0,
    opacity: props.opacity ?? 100,
    startArrowhead: null,
    endArrowhead: cmd.type === 'line' ? null : 'arrow',
    roundness: null,
    startBinding: startEl ? { elementId: startEl.id, focus: 0, gap: 8 } : null,
    endBinding: endEl ? { elementId: endEl.id, focus: 0, gap: 8 } : null,
  }
}

function getBounds(element: any) {
  return {
    x: element.x,
    y: element.y,
    width: element.width || 0,
    height: element.height || 0,
  }
}

function formatSceneDescription(elements: readonly any[]) {
  const visible = elements.filter((e) => !e.isDeleted)
  return {
    count: visible.length,
    layouts: layoutRegistry.size > 0 ? Array.from(layoutRegistry.entries()).map(([id, meta]) => ({
      containerId: id,
      layout: meta.layout,
      gap: meta.gap,
      padding: meta.padding,
      childIds: meta.childIds,
    })) : undefined,
    elements: visible.map((e) => ({
      id: e.id,
      type: e.type,
      x: Math.round(e.x),
      y: Math.round(e.y),
      width: Math.round(e.width || 0),
      height: Math.round(e.height || 0),
      text: e.text || undefined,
      groupIds: e.groupIds?.length ? e.groupIds : undefined,
    })),
  }
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function resizeImageBlob(blob: Blob, maxDim: number): Promise<Blob> {
  const bitmap = await createImageBitmap(blob)
  const scale = Math.min(1, maxDim / bitmap.width, maxDim / bitmap.height)
  if (scale >= 1) {
    bitmap.close()
    return blob
  }
  const w = Math.round(bitmap.width * scale)
  const h = Math.round(bitmap.height * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(bitmap, 0, 0, w, h)
  bitmap.close()
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Resize failed'))), 'image/jpeg', 0.8)
  })
}

async function executeCommand(api: ExcalidrawImperativeAPI, cmd: ExcalidrawCommand, projectId?: number): Promise<any> {
  const convert = await getConvertFn()

  switch (cmd.action) {
    case 'create_element': {
      // Handle bound arrows with startId/endId
      if ((cmd.type === 'arrow' || cmd.type === 'line') && (cmd.startId || cmd.endId)) {
        const existing = api.getSceneElements()
        const arrowSkeleton = buildBoundArrow(cmd, existing)
        const [arrow] = convert([arrowSkeleton])

        // Update target elements' boundElements arrays
        const updatedElements = [...existing, arrow].map(e => {
          if ((e.id === cmd.startId || e.id === cmd.endId) && !e.isDeleted) {
            const bound = e.boundElements || []
            return { ...e, boundElements: [...bound, { id: arrow.id, type: 'arrow' }] }
          }
          return e
        })
        api.updateScene({ elements: updatedElements })
        return { success: true, action: cmd.action, elementId: arrow.id, bounds: getBounds(arrow) }
      }

      const skeleton = buildSkeleton(cmd)
      const elements = convert([skeleton])
      const existing = api.getSceneElements()

      // Center text in a referenced container
      const el = elements[0]
      if (cmd.centerInId) {
        const container = existing.find(e => e.id === cmd.centerInId && !e.isDeleted)
        if (container && el.width) {
          elements[0] = { ...el, x: container.x + ((container.width || 0) - el.width) / 2 }
        }
      }

      api.updateScene({ elements: [...existing, ...elements] })
      const primary = elements[0]
      return { success: true, action: cmd.action, elementId: primary.id, bounds: getBounds(primary) }
    }

    case 'batch_create_elements': {
      const elemDefs = cmd.elements || []
      const centerPairs: Array<{ elementTempId: string; containerRef: string }> = []

      // Separate arrow elements that use startId/endId (need special handling)
      const regularDefs: typeof elemDefs = []
      const boundArrowDefs: typeof elemDefs = []

      for (const elemDef of elemDefs) {
        if ((elemDef.type === 'arrow' || elemDef.type === 'line') && (elemDef.startId || elemDef.endId)) {
          boundArrowDefs.push(elemDef)
        } else {
          regularDefs.push(elemDef)
        }
      }

      const allSkeletons = regularDefs.map(elemDef => {
        const skeleton = buildSkeleton({ ...elemDef, action: 'create_element' })
        // Ensure every skeleton has an ID (needed for centerInId lookups with regenerateIds: false)
        if (!skeleton.id) skeleton.id = generateId()
        if (elemDef.centerInId) {
          centerPairs.push({ elementTempId: skeleton.id, containerRef: elemDef.centerInId })
        }
        return skeleton
      })

      const newElements = convert(allSkeletons)
      const existing = api.getSceneElements()

      // Resolve centerInId: find container by ID and center the text element horizontally
      if (centerPairs.length > 0) {
        const allOnCanvas = [...existing, ...newElements]
        for (const { elementTempId, containerRef } of centerPairs) {
          const elIdx = newElements.findIndex(e => e.id === elementTempId)
          const container = allOnCanvas.find(c => c.id === containerRef && !c.isDeleted)
          if (elIdx >= 0 && container && newElements[elIdx].width) {
            newElements[elIdx] = {
              ...newElements[elIdx],
              x: container.x + ((container.width || 0) - newElements[elIdx].width) / 2,
            }
          }
        }
      }

      // Process bound arrows after regular elements so they can reference elements from this batch
      const allSoFar = [...existing, ...newElements]
      for (const arrowDef of boundArrowDefs) {
        const arrowSkeleton = buildBoundArrow({ ...arrowDef, action: 'create_element' }, allSoFar)
        const [arrow] = convert([arrowSkeleton])

        // Update target elements' boundElements arrays
        for (let i = 0; i < newElements.length; i++) {
          if ((newElements[i].id === arrowDef.startId || newElements[i].id === arrowDef.endId) && !newElements[i].isDeleted) {
            const bound = newElements[i].boundElements || []
            newElements[i] = { ...newElements[i], boundElements: [...bound, { id: arrow.id, type: 'arrow' }] }
          }
        }

        newElements.push(arrow)
        allSoFar.push(arrow)
      }

      api.updateScene({ elements: [...existing, ...newElements] })
      return {
        success: true,
        action: cmd.action,
        elementIds: newElements.map((e: any) => e.id),
        count: newElements.length,
      }
    }

    case 'update_element': {
      if (!cmd.id) return { success: false, action: cmd.action, error: 'id is required' }
      const elements = api.getSceneElements()
      const updated = elements.map((e) => {
        if (e.id === cmd.id) {
          const props = cmd.properties || {}
          return { ...e, ...props, version: (e.version || 1) + 1, updated: Date.now() }
        }
        return e
      })
      api.updateScene({ elements: updated })
      const target = updated.find((e) => e.id === cmd.id)
      return target
        ? { success: true, action: cmd.action, elementId: cmd.id, bounds: getBounds(target) }
        : { success: false, action: cmd.action, error: `Element ${cmd.id} not found` }
    }

    case 'delete_element': {
      const idsToDelete = cmd.ids || (cmd.id ? [cmd.id] : [])
      if (!idsToDelete.length) return { success: false, action: cmd.action, error: 'id or ids required' }
      const elements = api.getSceneElements()
      const updated = elements.map((e) =>
        idsToDelete.includes(e.id) ? { ...e, isDeleted: true } : e
      )
      api.updateScene({ elements: updated })

      // Clean up layout registry for deleted elements
      for (const delId of idsToDelete) {
        if (layoutRegistry.has(delId)) {
          layoutRegistry.delete(delId)
        }
        // Remove from parent container's childIds
        layoutRegistry.forEach((meta) => {
          const idx = meta.childIds.indexOf(delId)
          if (idx >= 0) {
            meta.childIds.splice(idx, 1)
            meta.fillDirectives.delete(delId)
          }
        })
      }

      return { success: true, action: cmd.action, deletedIds: idsToDelete }
    }

    case 'get_element': {
      if (!cmd.id) return { success: false, action: cmd.action, error: 'id is required' }
      const element = api.getSceneElements().find((e) => e.id === cmd.id && !e.isDeleted)
      return element
        ? { success: true, action: cmd.action, element }
        : { success: false, action: cmd.action, error: `Element ${cmd.id} not found` }
    }

    case 'query_elements': {
      let results = api.getSceneElements().filter((e) => !e.isDeleted)
      if (cmd.type) results = results.filter((e) => e.type === cmd.type)
      if (cmd.textContains) {
        const search = cmd.textContains.toLowerCase()
        results = results.filter((e) => (e as any).text?.toLowerCase().includes(search))
      }
      if (cmd.bounds) {
        const b = cmd.bounds
        results = results.filter(
          (e) =>
            e.x >= b.x &&
            e.y >= b.y &&
            e.x + (e.width || 0) <= b.x + b.width &&
            e.y + (e.height || 0) <= b.y + b.height
        )
      }
      return {
        success: true,
        action: cmd.action,
        elements: results.map((e) => ({
          id: e.id,
          type: e.type,
          x: e.x,
          y: e.y,
          width: e.width,
          height: e.height,
          text: (e as any).text,
        })),
      }
    }

    case 'duplicate_elements': {
      const ids = cmd.ids || []
      if (!ids.length) return { success: false, action: cmd.action, error: 'ids required' }
      const ox = cmd.offsetX ?? 20
      const oy = cmd.offsetY ?? 20
      const elements = api.getSceneElements()
      const originals = elements.filter((e) => ids.includes(e.id) && !e.isDeleted)
      const copies = originals.map((e) => ({
        ...e,
        id: generateId(),
        x: e.x + ox,
        y: e.y + oy,
        version: 1,
        versionNonce: Math.floor(Math.random() * 2000000000),
        seed: Math.floor(Math.random() * 2000000000),
        updated: Date.now(),
      }))
      api.updateScene({ elements: [...elements, ...copies] })
      return { success: true, action: cmd.action, elementIds: copies.map((c) => c.id) }
    }

    case 'align_elements': {
      const ids = cmd.ids || []
      if (!ids.length || !cmd.alignment) return { success: false, action: cmd.action, error: 'ids and alignment required' }
      const elements = api.getSceneElements()
      const targets = elements.filter((e) => ids.includes(e.id) && !e.isDeleted)
      if (!targets.length) return { success: false, action: cmd.action, error: 'No matching elements' }

      let ref: number
      switch (cmd.alignment) {
        case 'left': ref = Math.min(...targets.map((e) => e.x)); break
        case 'right': ref = Math.max(...targets.map((e) => e.x + (e.width || 0))); break
        case 'center': ref = (Math.min(...targets.map((e) => e.x)) + Math.max(...targets.map((e) => e.x + (e.width || 0)))) / 2; break
        case 'top': ref = Math.min(...targets.map((e) => e.y)); break
        case 'bottom': ref = Math.max(...targets.map((e) => e.y + (e.height || 0))); break
        case 'middle': ref = (Math.min(...targets.map((e) => e.y)) + Math.max(...targets.map((e) => e.y + (e.height || 0)))) / 2; break
        default: return { success: false, action: cmd.action, error: `Invalid alignment: ${cmd.alignment}` }
      }

      const updated = elements.map((e) => {
        if (!ids.includes(e.id) || e.isDeleted) return e
        switch (cmd.alignment) {
          case 'left': return { ...e, x: ref }
          case 'right': return { ...e, x: ref - (e.width || 0) }
          case 'center': return { ...e, x: ref - (e.width || 0) / 2 }
          case 'top': return { ...e, y: ref }
          case 'bottom': return { ...e, y: ref - (e.height || 0) }
          case 'middle': return { ...e, y: ref - (e.height || 0) / 2 }
          default: return e
        }
      })
      api.updateScene({ elements: updated })
      return { success: true, action: cmd.action, alignment: cmd.alignment, count: targets.length }
    }

    case 'distribute_elements': {
      const ids = cmd.ids || []
      if (!ids.length || !cmd.direction) return { success: false, action: cmd.action, error: 'ids and direction required' }
      const elements = api.getSceneElements()
      const targets = elements.filter((e) => ids.includes(e.id) && !e.isDeleted)
      if (targets.length < 3) return { success: true, action: cmd.action, count: targets.length }

      const sorted = [...targets].sort((a, b) =>
        cmd.direction === 'horizontal' ? a.x - b.x : a.y - b.y
      )
      const first = sorted[0]
      const last = sorted[sorted.length - 1]
      const totalSpace =
        cmd.direction === 'horizontal'
          ? last.x + (last.width || 0) - first.x
          : last.y + (last.height || 0) - first.y
      const totalSize = sorted.reduce(
        (sum, e) => sum + (cmd.direction === 'horizontal' ? e.width || 0 : e.height || 0),
        0
      )
      const gap = (totalSpace - totalSize) / (sorted.length - 1)

      let pos = cmd.direction === 'horizontal' ? first.x : first.y
      const posMap = new Map<string, number>()
      for (const e of sorted) {
        posMap.set(e.id, pos)
        pos += (cmd.direction === 'horizontal' ? e.width || 0 : e.height || 0) + gap
      }

      const updated = elements.map((e) => {
        const newPos = posMap.get(e.id)
        if (newPos == null) return e
        return cmd.direction === 'horizontal' ? { ...e, x: newPos } : { ...e, y: newPos }
      })
      api.updateScene({ elements: updated })
      return { success: true, action: cmd.action, direction: cmd.direction, count: targets.length }
    }

    case 'group_elements': {
      const ids = cmd.ids || []
      if (!ids.length) return { success: false, action: cmd.action, error: 'ids required' }
      const gid = generateId()
      const elements = api.getSceneElements()
      const updated = elements.map((e) => {
        if (ids.includes(e.id) && !e.isDeleted) {
          return { ...e, groupIds: [...(e.groupIds || []), gid] }
        }
        return e
      })
      api.updateScene({ elements: updated })
      return { success: true, action: cmd.action, groupId: gid, count: ids.length }
    }

    case 'ungroup_elements': {
      if (!cmd.groupId) return { success: false, action: cmd.action, error: 'groupId required' }
      const elements = api.getSceneElements()
      const updated = elements.map((e) => {
        if (e.groupIds?.includes(cmd.groupId!)) {
          return { ...e, groupIds: e.groupIds.filter((g: string) => g !== cmd.groupId) }
        }
        return e
      })
      api.updateScene({ elements: updated })
      return { success: true, action: cmd.action, groupId: cmd.groupId }
    }

    case 'clear_canvas': {
      api.updateScene({ elements: [] })
      layoutRegistry.clear()
      return { success: true, action: cmd.action }
    }

    case 'save_and_clear_canvas': {
      try {
        const elements = api.getSceneElements().filter((e) => !e.isDeleted)
        if (!elements.length) {
          return { success: true, action: cmd.action, message: 'Canvas was already empty' }
        }

        const { exportToBlob } = await import('@excalidraw/excalidraw')
        const rawBlob = await exportToBlob({
          elements,
          appState: { ...api.getAppState(), exportBackground: true },
          files: api.getFiles(),
          mimeType: 'image/jpeg',
          quality: 1,
        })
        const blob = await resizeImageBlob(rawBlob, 3200)

        // Upload to assets (fire-and-forget)
        const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
        const formData = new FormData()
        formData.append('file', blob, `prototype-${Date.now()}.jpg`)
        fetch(`/projects/${projectId}/sketch/save_screenshot`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': csrfToken },
          body: formData,
        }).catch((err) => console.error('Failed to save canvas screenshot:', err))

        api.updateScene({ elements: [] })
        layoutRegistry.clear()
        return { success: true, action: cmd.action, message: 'Canvas saved to assets and cleared' }
      } catch (err) {
        return { success: false, action: cmd.action, error: `Save and clear failed: ${err}` }
      }
    }

    case 'describe_scene': {
      const elements = api.getSceneElements()
      return { success: true, action: cmd.action, scene: formatSceneDescription(elements) }
    }

    case 'get_canvas_screenshot': {
      try {
        const { exportToBlob } = await import('@excalidraw/excalidraw')
        const elements = api.getSceneElements().filter((e) => !e.isDeleted)
        if (!elements.length) {
          return { success: true, action: cmd.action, screenshot: null, message: 'Canvas is empty' }
        }
        const rawBlob = await exportToBlob({
          elements,
          appState: { ...api.getAppState(), exportBackground: true },
          files: api.getFiles(),
          mimeType: 'image/jpeg',
          quality: 1,
        })
        const blob = await resizeImageBlob(rawBlob, 1600)
        const base64 = await blobToBase64(blob)
        return { success: true, action: cmd.action, screenshot: base64 }
      } catch (err) {
        return { success: false, action: cmd.action, error: `Screenshot failed: ${err}` }
      }
    }

    case 'set_viewport': {
      if (cmd.scrollToContent) {
        api.scrollToContent()
      }
      if (cmd.zoom != null || cmd.x != null || cmd.y != null) {
        const appState = api.getAppState()
        api.updateScene({
          appState: {
            ...appState,
            ...(cmd.zoom != null ? { zoom: { value: cmd.zoom as any } } : {}),
            ...(cmd.x != null ? { scrollX: -cmd.x } : {}),
            ...(cmd.y != null ? { scrollY: -cmd.y } : {}),
          },
        })
      }
      return { success: true, action: cmd.action }
    }

    case 'snapshot_scene': {
      if (!cmd.name) return { success: false, action: cmd.action, error: 'name required' }
      snapshots[cmd.name] = {
        elements: [...api.getSceneElements()],
        appState: { ...api.getAppState() },
      }
      return { success: true, action: cmd.action, name: cmd.name }
    }

    case 'restore_snapshot': {
      if (!cmd.name) return { success: false, action: cmd.action, error: 'name required' }
      const snap = snapshots[cmd.name]
      if (!snap) return { success: false, action: cmd.action, error: `Snapshot "${cmd.name}" not found` }
      api.updateScene({ elements: snap.elements })
      return { success: true, action: cmd.action, name: cmd.name }
    }

    // ===== Auto-layout containers =====

    case 'create_layout_container': {
      const layout = cmd.layout
      if (!layout) return { success: false, action: cmd.action, error: 'layout (vertical|horizontal) is required' }

      const fitContent = cmd.height === 'fit_content'
      const containerW = (typeof cmd.width === 'number' ? cmd.width : 400) as number
      const containerH = (typeof cmd.height === 'number' ? cmd.height : 600) as number

      // Recursively build all skeletons (handles nested layout containers)
      const { skeletons, containerIds } = buildLayoutTree({
        x: cmd.x || 0,
        y: cmd.y || 0,
        width: containerW,
        height: containerH,
        layout,
        align: cmd.align,
        justify: cmd.justify,
        gap: cmd.gap,
        padding: cmd.padding,
        preset: cmd.preset,
        properties: cmd.properties,
        children: cmd.children,
        fitContent,
        id: cmd.id,
      })

      // Convert all skeletons — gives elements their actual dimensions
      const newElements = convert(skeletons)

      // Restore intended dimensions on text elements — convertToExcalidrawElements
      // auto-sizes text to natural content width, which breaks layout containers where
      // text must occupy a specific column width (e.g., table cells)
      for (const el of newElements) {
        if (el.type !== 'text') continue
        const skeleton = skeletons.find((s: any) => s.id === el.id)
        if (!skeleton) continue
        if (typeof skeleton.width === 'number' && skeleton.width > 0) el.width = skeleton.width
        if (typeof skeleton.height === 'number' && skeleton.height > 0) el.height = skeleton.height
      }

      const existing = api.getSceneElements()
      let allElements = [...existing, ...newElements]

      // Apply layouts top-down: parent first, then nested containers
      for (const cId of containerIds) {
        allElements = applyLayout(allElements, cId)
      }

      // Second pass for fitContent containers: re-resolve fill children after shrink
      for (const cId of containerIds) {
        if (layoutRegistry.get(cId)?.fitContent) {
          allElements = applyLayout(allElements, cId)
        }
      }

      api.updateScene({ elements: allElements })

      const rootContainerId = containerIds[0]
      const containerEl = allElements.find((e: any) => e.id === rootContainerId)
      return {
        success: true,
        action: cmd.action,
        containerId: rootContainerId,
        childIds: layoutRegistry.get(rootContainerId)?.childIds || [],
        bounds: getBounds(containerEl || { x: cmd.x || 0, y: cmd.y || 0, width: containerW, height: containerH }),
      }
    }

    // ===== Find empty space =====

    case 'find_empty_space': {
      const visible = api.getSceneElements().filter(e => !e.isDeleted)
      const reqW = (typeof cmd.width === 'number' ? cmd.width : 1200) as number
      const reqH = (typeof cmd.height === 'number' ? cmd.height : 800) as number
      const pad = cmd.padding ?? 100
      const dir = cmd.findDirection || 'auto'

      if (!visible.length) {
        return { success: true, action: cmd.action, x: 50, y: 50, width: reqW, height: reqH }
      }

      const { minX, minY, maxX, maxY } = getElementsBounds(visible)

      let resultX: number, resultY: number
      if (dir === 'right' || (dir === 'auto' && maxX + pad + reqW <= 10000)) {
        resultX = maxX + pad
        resultY = minY
      } else {
        resultX = minX
        resultY = maxY + pad
      }

      return { success: true, action: cmd.action, x: Math.round(resultX), y: Math.round(resultY), width: reqW, height: reqH }
    }

    case 'check_layout_problems': {
      const elements = [...api.getSceneElements()]
      const visible = elements.filter(e => !e.isDeleted)
      const problems: any[] = []
      let modified = false

      // 1. Invisible text — auto-fix contrast (dark-on-dark OR light-on-light)
      for (const el of visible) {
        if (el.type !== 'text') continue
        let parentBg: string | null = null
        layoutRegistry.forEach((meta, cId) => {
          if (meta.childIds.includes(el.id)) {
            const container = visible.find(c => c.id === cId)
            if (container) parentBg = container.backgroundColor
          }
        })
        if (!parentBg) {
          const parent = visible.find(p =>
            p.boundElements?.some((b: any) => b.id === el.id && b.type === 'text')
          )
          if (parent) parentBg = parent.backgroundColor
        }
        if (!parentBg) continue
        const newColor = getContrastFixedColor(parentBg, el.strokeColor)
        if (newColor && newColor !== el.strokeColor) {
          const idx = elements.findIndex(e => e.id === el.id)
          if (idx >= 0) {
            elements[idx] = { ...elements[idx], strokeColor: newColor, version: (elements[idx].version || 1) + 1, updated: Date.now() }
            problems.push({ problem: 'invisible_text', fixed: true, elementId: el.id })
            modified = true
          }
        }
      }

      // 2. Container overflow — auto-fix by re-running applyLayout
      layoutRegistry.forEach((meta, containerId) => {
        const container = visible.find(e => e.id === containerId)
        if (!container) return
        for (const childId of meta.childIds) {
          const child = visible.find(e => e.id === childId)
          if (!child) continue
          if (child.x + (child.width || 0) > container.x + (container.width || 0) + 5 ||
              child.y + (child.height || 0) > container.y + (container.height || 0) + 5) {
            applyLayout(elements, containerId)
            problems.push({ problem: 'container_overflow', fixed: true, containerId })
            modified = true
            return
          }
        }
      })

      // 3. Empty containers — auto-fix by deleting
      layoutRegistry.forEach((meta, containerId) => {
        const hasVisibleChildren = meta.childIds.some(id => visible.some(e => e.id === id))
        if (!hasVisibleChildren) {
          const idx = elements.findIndex(e => e.id === containerId)
          if (idx >= 0) {
            elements[idx] = { ...elements[idx], isDeleted: true }
            layoutRegistry.delete(containerId)
            problems.push({ problem: 'empty_container', fixed: true, containerId })
            modified = true
          }
        }
      })

      // 4. Overlapping elements — report only
      const shapes = visible.filter(e => e.type !== 'arrow' && e.type !== 'line' && !(e.containerId && e.type === 'text'))
      for (let i = 0; i < shapes.length; i++) {
        for (let j = i + 1; j < shapes.length; j++) {
          const a = shapes[i], b = shapes[j]
          if (a.groupIds?.some((g: string) => b.groupIds?.includes(g))) continue
          let isLayoutSiblings = false
          layoutRegistry.forEach((meta) => {
            if (meta.childIds.includes(a.id) && meta.childIds.includes(b.id)) isLayoutSiblings = true
          })
          if (isLayoutSiblings) continue
          if (getOverlapArea(a, b) > 100) {
            problems.push({ problem: 'overlapping', fixed: false, elementIds: [a.id, b.id] })
          }
        }
      }

      if (modified) {
        api.updateScene({ elements })
      }

      return { success: true, action: cmd.action, problems, fixedCount: problems.filter((p: any) => p.fixed).length, totalCount: problems.length }
    }

    default:
      return { success: false, action: cmd.action, error: `Unknown action: ${cmd.action}` }
  }
}

/**
 * Hook to handle excalidraw commands via ActionCable
 *
 * Flow:
 * 1. cayu-pilot sends commands to cayu-ai-mvp API
 * 2. API broadcasts via ActionCable to this hook
 * 3. Hook executes commands against Excalidraw API
 * 4. Hook sends results back via ActionCable to complete the API request
 */
export function useExcalidrawUse({ projectId, excalidrawAPI, onCommandsExecuted, onSwitchToSketch }: UseExcalidrawUseOptions) {
  const [isActive, setIsActive] = useState(false)
  const [currentAction, setCurrentAction] = useState<string | null>(null)

  const consumerRef = useRef<Cable | null>(null)
  const channelRef = useRef<any>(null)
  const excalidrawAPIRef = useRef(excalidrawAPI)
  const onCommandsExecutedRef = useRef(onCommandsExecuted)
  const onSwitchToSketchRef = useRef(onSwitchToSketch)

  // Keep refs up to date
  useEffect(() => {
    excalidrawAPIRef.current = excalidrawAPI
    onCommandsExecutedRef.current = onCommandsExecuted
    onSwitchToSketchRef.current = onSwitchToSketch
  })

  // Set up ActionCable subscription
  useEffect(() => {
    consumerRef.current = createConsumer()

    channelRef.current = consumerRef.current.subscriptions.create(
      {
        channel: 'ExcalidrawChannel',
        project_id: projectId,
      },
      {
        received: async (data: { type: string; session_id: string; commands?: ExcalidrawCommand[] }) => {
          if (data.type === 'execute') {
            const { session_id, commands } = data
            let api = excalidrawAPIRef.current

            if (!commands) {
              return
            }

            if (!api) {
              // Canvas not mounted — try switching to sketch view and wait for API
              if (onSwitchToSketchRef.current) {
                await onSwitchToSketchRef.current()
                // Wait for Excalidraw API to become available (up to 3s)
                for (let wait = 0; wait < 30; wait++) {
                  await new Promise(r => setTimeout(r, 100))
                  if (excalidrawAPIRef.current) break
                }
                api = excalidrawAPIRef.current
              }

              if (!api) {
                if (channelRef.current) {
                  channelRef.current.perform('results', {
                    session_id,
                    results: { results: [{ success: false, action: 'error', error: 'Excalidraw API not available. Canvas may not be mounted.' }] },
                  })
                }
                return
              }
            }

            setIsActive(true)
            const results: any[] = []

            try {
              for (let i = 0; i < commands.length; i++) {
                const cmd = commands[i]
                setCurrentAction(`${cmd.action} (${i + 1}/${commands.length})`)
                const result = await executeCommand(api, cmd, projectId)
                results.push({ ...result, step: i })
              }
            } catch (err) {
              results.push({ success: false, action: 'error', error: String(err) })
            }

            // Send results back via ActionCable
            if (channelRef.current) {
              channelRef.current.perform('results', {
                session_id,
                results: { results },
              })
            }

            setIsActive(false)
            setCurrentAction(null)

            // Trigger save after AI commands modify the canvas
            onCommandsExecutedRef.current?.()
          }
        },
        connected: () => {
          console.log('[Excalidraw] ActionCable connected, project:', projectId)
        },
        disconnected: () => {
          console.warn('[Excalidraw] ActionCable disconnected')
        },
        rejected: () => {
          console.error('[Excalidraw] ActionCable subscription rejected, project:', projectId)
        },
      }
    )

    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
      }
      if (consumerRef.current) {
        consumerRef.current.disconnect()
      }
    }
  }, [projectId])

  const stopExcalidraw = useCallback(() => {
    setIsActive(false)
    setCurrentAction(null)
  }, [])

  return {
    isActive,
    currentAction,
    stopExcalidraw,
  }
}
